import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  AlertTriangle, Check, ChevronDown, ChevronRight, Clock, Copy,
  FileText, Folder, FolderInput, GitCompare, History, Pencil, Quote, Trash2,
} from 'lucide-react';
import { CitationPanel } from '../components/kb/CitationPanel';
import { KbTreePanel } from '../components/kb/KbTreePanel';
import { CitationSourceDrawer } from '../components/kb/CitationSourceDrawer';
import { CitedMarkdown } from '../components/kb/CitedMarkdown';
import { LineDiffView, lineDiffStats } from '../components/ui/diff';
import { Button } from '../components/ui/button';
import { Dialog } from '../components/ui/dialog';
import { Drawer } from '../components/ui/drawer';
import { Input, Select, Textarea } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  api, type KbChange, type KbCitation, type KbNodeDetail, type KbRevision,
  type KbTreeNode,
} from '../lib/api';
import { useKbTree } from '../lib/kb-tree';
import { cn, formatDate } from '../lib/utils';
import { Markdown } from '../components/ui/markdown';

// ─── Change history ───────────────────────────────────────────────────────────

const CHANGE_KIND_TONE: Record<KbChange['kind'], string> = {
  CREATE: 'text-green-600 dark:text-green-400',
  UPDATE: 'text-orange-600 dark:text-orange-400',
  DELETE: 'text-red-600 dark:text-red-400',
  MERGE: 'text-purple-600 dark:text-purple-400',
  MOVE: 'text-blue-600 dark:text-blue-400',
};

/**
 * One recorded mutation of this node, expandable into its diff.
 *
 * Richer than the revision chain it sits alongside: a change carries the agent's reasoning, its
 * confidence, and its review outcome, so this is the view that answers "why does the page say
 * this now" rather than only "what did it say before".
 */
function NodeChangeEntry({
  change, defaultOpen, projectSlug,
}: {
  change: KbChange;
  defaultOpen: boolean;
  projectSlug: string;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const before = change.beforeBody ?? '';
  const after = change.afterBody ?? '';
  const stats = useMemo(() => lineDiffStats(before, after), [before, after]);
  const tone = CHANGE_KIND_TONE[change.kind];

  return (
    <div className="border border-border rounded-lg overflow-hidden bg-card">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-surface-subtle transition-colors"
      >
        {open ? <ChevronDown size={12} className="text-ink-faint flex-shrink-0" /> : <ChevronRight size={12} className="text-ink-faint flex-shrink-0" />}
        <span className={cn('text-[10px] font-semibold uppercase tracking-wide flex-shrink-0', tone)}>
          {change.kind}
        </span>
        <span className="text-xs text-ink truncate flex-1">{change.reasoning}</span>
        <span className="flex items-center gap-2 flex-shrink-0">
          {(stats.added > 0 || stats.removed > 0) && (
            <span className="text-[10px] font-mono">
              <span className="text-green-600 dark:text-green-400">+{stats.added}</span>{' '}
              <span className="text-red-500 dark:text-red-400">−{stats.removed}</span>
            </span>
          )}
          {change.reviewStatus === 'PENDING' && <Badge variant="warning">review</Badge>}
          {change.reviewStatus === 'REJECTED' && <Badge variant="danger">reverted</Badge>}
          {change.reviewStatus === 'KEPT_BOTH' && <Badge variant="info">kept both</Badge>}
          {change.reviewStatus === 'SUPERSEDED' && <Badge variant="info">superseded by fix</Badge>}
          <span className="text-[10px] text-ink-faint">{formatDate(change.createdAt)}</span>
        </span>
      </button>

      {open && (
        <div className="border-t border-border-subtle px-3 py-3 space-y-2">
          <div className="flex items-center gap-3 text-[10px] text-ink-faint flex-wrap">
            <span>{Math.round(change.confidence * 100)}% confident</span>
            {change.model && <span className="font-mono">{change.model}</span>}
            {change.fromPath && <span>from {change.fromPath}</span>}
            {change.mergedFromPaths.length > 0 && <span>merged {change.mergedFromPaths.join(', ')}</span>}
            {change.reviewStatus === 'PENDING' && (
              <Link
                to={`/p/${projectSlug}/kb-review`}
                className="text-orange-500 hover:text-orange-700 transition-colors"
              >
                Review this change
              </Link>
            )}
          </div>

          {change.kind === 'MOVE' ? (
            <p className="text-xs text-ink-secondary">
              Re-filed from <span className="font-mono text-ink">{change.fromPath}</span>. Body unchanged.
            </p>
          ) : before || after ? (
            <LineDiffView oldText={before} newText={after} />
          ) : (
            <p className="text-xs text-ink-faint">No body content recorded for this change.</p>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Node detail ──────────────────────────────────────────────────────────────

function RevisionHistory({
  node, projectId, onRestored,
}: {
  node: KbNodeDetail;
  projectId: string;
  onRestored: () => void;
}) {
  const [viewing, setViewing] = useState<KbRevision | null>(null);
  const [restoring, setRestoring] = useState(false);

  const restore = async (revision: KbRevision) => {
    setRestoring(true);
    try {
      await api.kb.restoreRevision(projectId, node.path, revision.id);
      setViewing(null);
      onRestored();
    } finally {
      setRestoring(false);
    }
  };

  return (
    <>
      <div className="space-y-1">
        {node.revisions.map((r) => (
          <div key={r.id} className="flex items-center gap-2 text-[11px] group">
            <button
              onClick={() => setViewing(r)}
              className="flex items-center gap-2 flex-1 min-w-0 text-left hover:text-orange-500 transition-colors"
            >
              <span className="font-mono text-ink-faint w-8 flex-shrink-0">v{r.version}</span>
              <span className={cn(
                'px-1.5 rounded-full text-[9px] font-medium flex-shrink-0',
                r.generatedByAI
                  ? 'bg-orange-100 dark:bg-orange-950/50 text-orange-600 dark:text-orange-400'
                  : 'bg-surface-muted text-ink-muted',
              )}>
                {r.generatedByAI ? 'agent' : 'human'}
              </span>
              <span className="text-ink-secondary truncate flex-1">{r.changeSummary ?? r.title}</span>
              <span className="text-ink-faint flex-shrink-0">{formatDate(r.createdAt)}</span>
            </button>
          </div>
        ))}
      </div>

      {/* A drawer, not a modal: reading an old revision is something you do *against* the current
          page, and a centred modal covers the very text you're comparing with. */}
      <Drawer
        open={viewing !== null}
        onClose={() => setViewing(null)}
        title={viewing ? `v${viewing.version} — ${viewing.title}` : ''}
        className="max-w-xl"
      >
        {viewing && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant={viewing.generatedByAI ? 'warning' : 'default'}>
                {viewing.generatedByAI ? 'Written by the ingestion agent' : 'Written by hand'}
              </Badge>
              {viewing.id === node.currentRevisionId && <Badge variant="success">Current</Badge>}
              <span className="text-[11px] text-ink-faint">{formatDate(viewing.createdAt)}</span>
            </div>

            {viewing.changeSummary && (
              <p className="text-[11px] text-ink-muted italic leading-relaxed">{viewing.changeSummary}</p>
            )}

            {/* The drawer body scrolls as a whole, so no nested scroll region to trap the wheel. */}
            <div className="border border-border-subtle rounded-md p-3 bg-surface-subtle">
              <Markdown>{viewing.body || '_(empty)_'}</Markdown>
            </div>

            {viewing.id !== node.currentRevisionId && (
              <div className="flex justify-end pt-1">
                <Button variant="primary" disabled={restoring} onClick={() => restore(viewing)}>
                  {restoring ? 'Restoring…' : 'Restore this version'}
                </Button>
              </div>
            )}
          </div>
        )}
      </Drawer>
    </>
  );
}

function NodeDetail({
  node, projectId, projectSlug, allNodes, onReload, onDeleted, onSelect,
}: {
  node: KbNodeDetail;
  projectId: string;
  projectSlug: string;
  allNodes: KbTreeNode[];
  onReload: () => void;
  onDeleted: () => void;
  onSelect: (path: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(node.title);
  const [summary, setSummary] = useState(node.summary ?? '');
  const [body, setBody] = useState(node.body);
  /** Why the edit was made — recorded as part of the edit's evidence, never on the page itself. */
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [view, setView] = useState<'content' | 'changes' | 'sources'>('content');
  /** Shared by the Sources list and the inline markers, so both open one dialog. */
  const [selectedCitation, setSelectedCitation] = useState<KbCitation | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [moveTarget, setMoveTarget] = useState('');
  const [moving, setMoving] = useState(false);

  useEffect(() => {
    setEditing(false);
    setTitle(node.title);
    setSummary(node.summary ?? '');
    setBody(node.body);
    setNote('');
    setError(null);
    setView('content');
    setSelectedCitation(null);
  }, [node.id, node.currentRevisionId, node.title, node.summary, node.body]);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.kb.updateNode(projectId, node.path, {
        title: title.trim(),
        summary: summary.trim(),
        body,
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      setEditing(false);
      onReload();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setDeleting(true);
    try {
      await api.kb.deleteNode(projectId, node.path);
      onDeleted();
    } catch (e: any) {
      setError(e.message);
      setDeleting(false);
    } finally {
      setConfirmDelete(false);
    }
  };

  const move = async () => {
    setMoving(true);
    setError(null);
    try {
      const moved = await api.kb.moveNode(projectId, node.path, { newParentPath: moveTarget || null });
      setMoveOpen(false);
      onSelect(moved.path);
      onReload();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setMoving(false);
    }
  };

  const crumbs = node.path.split('/');
  const pending = node.changes.filter((c) => c.reviewStatus === 'PENDING');
  const citationCount = node.citations.current.length + node.citations.superseded.length;

  // A node can't be moved into itself or its own subtree, so those are excluded here rather
  // than left for the server to reject after the user has already picked one.
  const moveCandidates = allNodes.filter(
    (candidate) => candidate.path !== node.path && !candidate.path.startsWith(`${node.path}/`),
  );

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="px-7 pt-5 pb-4 border-b border-border-subtle flex-shrink-0">
        <div className="flex items-center gap-1 text-[11px] text-ink-faint mb-2 flex-wrap">
          {crumbs.map((crumb, i) => (
            <span key={i} className="flex items-center gap-1">
              {i > 0 && <ChevronRight size={9} />}
              <button
                onClick={() => onSelect(crumbs.slice(0, i + 1).join('/'))}
                className="hover:text-orange-500 transition-colors font-mono"
              >
                {crumb}
              </button>
            </span>
          ))}
        </div>

        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            {editing ? (
              <Input value={title} onChange={(e) => setTitle(e.target.value)} className="text-lg font-bold" autoFocus />
            ) : (
              <h1 className="text-lg font-bold text-ink leading-tight">{node.title}</h1>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {editing ? (
              <>
                <Button onClick={() => setEditing(false)}>Cancel</Button>
                <Button variant="primary" onClick={save} disabled={saving || !title.trim()}>
                  {saving ? 'Saving…' : 'Save'}
                </Button>
              </>
            ) : (
              <>
                <button
                  onClick={() => { navigator.clipboard.writeText(node.body); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-ink-muted border border-border rounded-md hover:text-ink hover:border-border-strong transition-colors"
                >
                  {copied ? <><Check size={12} className="text-green-500" />Copied</> : <><Copy size={12} />Copy</>}
                </button>
                <button
                  onClick={() => { setMoveTarget(crumbs.slice(0, -1).join('/')); setMoveOpen(true); }}
                  title="Move to another parent"
                  className="flex items-center justify-center w-8 h-8 text-ink-faint border border-border rounded-md hover:text-ink transition-colors"
                >
                  <FolderInput size={13} />
                </button>
                <Button variant="primary" onClick={() => setEditing(true)}>
                  <Pencil size={12} className="mr-1.5" />Edit
                </Button>
                <button
                  onClick={() => setConfirmDelete(true)}
                  title="Delete node"
                  className="flex items-center justify-center w-8 h-8 text-ink-faint border border-border rounded-md hover:text-red-500 hover:border-red-200 dark:hover:border-red-900 transition-colors"
                >
                  <Trash2 size={13} />
                </button>
              </>
            )}
          </div>
        </div>

        {editing ? (
          <div className="mt-3 space-y-3">
            <div>
              <label className="block text-xs font-medium text-ink-muted mb-1.5">
                Summary <span className="text-ink-faint font-normal">(one line — this is what retrieval routes on)</span>
              </label>
              <Input value={summary} onChange={(e) => setSummary(e.target.value)} />
            </div>
            <div>
              <label className="block text-xs font-medium text-ink-muted mb-1.5">
                Why this edit? <span className="text-ink-faint font-normal">(optional — recorded with your edit's evidence, not on the page)</span>
              </label>
              <Input value={note} onChange={(e) => setNote(e.target.value)} />
            </div>
          </div>
        ) : (
          node.summary && <p className="mt-2 text-xs text-ink-muted italic">{node.summary}</p>
        )}

        <div className="flex items-center gap-2 flex-wrap mt-3">
          <Badge variant="warning">{node.kind.toLowerCase()}</Badge>
          {node.kbType && <Badge variant="info">{node.kbType}</Badge>}
          {node.tags.map((tag) => <Badge key={tag}>{tag}</Badge>)}
          <span className="text-[11px] text-ink-faint flex items-center gap-1">
            <Clock size={10} />{formatDate(node.updatedAt)}
          </span>
        </div>

        {pending.length > 0 && (
          <div className="mt-3 flex items-start gap-2 text-[11px] bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded px-3 py-2">
            <AlertTriangle size={12} className="text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
            <span className="text-amber-800 dark:text-amber-300">
              {pending.length} agent change{pending.length === 1 ? '' : 's'} on this node {pending.length === 1 ? 'is' : 'are'} live
              but awaiting review — see the Review tab.
            </span>
          </div>
        )}

        {error && (
          <p className="mt-3 text-xs text-danger-text bg-danger-bg border border-danger-ring rounded px-3 py-1.5">{error}</p>
        )}
      </div>

      {/* Content / Changes / Sources. Real tabs rather than pills: they switch what the pane below
          shows, so the underline needs to read as attached to it. */}
      {!editing && (
        <div
          role="tablist"
          aria-label="Node view"
          className="px-7 flex items-center gap-5 border-b border-border-subtle flex-shrink-0"
        >
          {(
            [
              ['content', 'Content', FileText, null],
              ['changes', 'Changes', GitCompare, node.changes.length],
              ['sources', 'Sources', Quote, citationCount],
            ] as const
          ).map(([value, label, Icon, count]) => {
            const active = view === value;
            return (
              <button
                key={value}
                role="tab"
                aria-selected={active}
                onClick={() => setView(value)}
                className={cn(
                  'flex items-center gap-1.5 pt-2.5 pb-2 -mb-px border-b-2 text-xs font-medium transition-colors',
                  active
                    ? 'border-orange-500 text-orange-600 dark:text-orange-400'
                    : 'border-transparent text-ink-muted hover:text-ink hover:border-border-strong',
                )}
              >
                <Icon size={12} />
                {label}
                {count !== null && count > 0 && (
                  <span
                    className={cn(
                      'text-[10px] tabular-nums px-1.5 py-0.5 rounded-full font-semibold',
                      active
                        ? 'bg-orange-100 dark:bg-orange-950/60 text-orange-600 dark:text-orange-400'
                        : 'bg-surface-muted text-ink-faint',
                    )}
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Body */}
      <div className="flex-1 overflow-y-auto">
        <div className="px-7 py-5">
          {editing ? (
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={26}
              className="font-mono text-xs"
              placeholder="Markdown body…"
            />
          ) : view === 'changes' ? (
            node.changes.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-14 text-center">
                <GitCompare size={26} className="text-border mb-3" />
                <p className="text-sm text-ink-faint mb-1">No recorded changes</p>
                <p className="text-xs text-ink-faint max-w-xs">
                  Every edit — by the agent or by hand — shows up here as a diff with the reasoning
                  behind it.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="space-y-2">
                  <p className="flex items-center gap-1.5 text-[11px] text-ink-faint">
                    <History size={11} />
                    Newest first — each entry is what that edit changed, and why
                  </p>
                  {node.changes.map((change, i) => (
                    <NodeChangeEntry
                      key={change.id}
                      change={change}
                      defaultOpen={i === 0}
                      projectSlug={projectSlug}
                    />
                  ))}
                </div>

                {/* The revision chain sits under the changes rather than in its own tab: it is the
                    same history at a lower level, and it is the only place a restore can act. */}
                <div className="border-t border-border-subtle pt-3">
                  <p className="text-[10px] font-semibold text-ink-faint uppercase tracking-[0.07em] mb-2">
                    Revisions ({node.revisions.length})
                  </p>
                  <RevisionHistory node={node} projectId={projectId} onRestored={onReload} />
                </div>
              </div>
            )
          ) : view === 'sources' ? (
            <CitationPanel citations={node.citations} onOpen={setSelectedCitation} />
          ) : node.body.trim() ? (
            <CitedMarkdown
              body={node.body}
              citations={node.citations.current}
              onCitationClick={setSelectedCitation}
            />
          ) : (
            <div className="flex flex-col items-center justify-center py-14 text-center">
              <FileText size={28} className="text-border mb-3" />
              <p className="text-sm text-ink-faint mb-1">No content yet</p>
              <p className="text-xs text-ink-faint max-w-xs">
                Ingest something, or click Edit to write it yourself.
              </p>
            </div>
          )}
        </div>

        {node.children.length > 0 && (
          <div className="px-7 pb-8">
            <p className="text-[10px] font-semibold text-ink-faint uppercase tracking-[0.07em] mb-2">
              Sections ({node.children.length})
            </p>
            <div className="border border-border rounded-lg overflow-hidden divide-y divide-border-subtle">
              {node.children.map((child) => (
                <button
                  key={child.id}
                  onClick={() => onSelect(child.path)}
                  className="w-full flex items-start gap-2 px-3 py-2.5 text-left hover:bg-surface-subtle transition-colors"
                >
                  <FileText size={12} className="text-ink-faint mt-0.5 flex-shrink-0" />
                  <span className="min-w-0 flex-1">
                    <span className="block text-xs font-medium text-ink">{child.title}</span>
                    {child.summary && <span className="block text-[11px] text-ink-muted truncate">{child.summary}</span>}
                  </span>
                  <span className="text-[10px] text-ink-faint flex-shrink-0 mt-0.5">{child.bodyChars} chars</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <CitationSourceDrawer
        citation={selectedCitation}
        projectId={projectId}
        onClose={() => setSelectedCitation(null)}
      />

      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)} title="Delete node">
        <div className="space-y-4">
          <p className="text-sm text-ink-muted">
            Delete <span className="font-medium text-ink">"{node.title}"</span>
            {node.children.length > 0 && ` and its ${node.children.length} section${node.children.length === 1 ? '' : 's'}`}?
            The revision history goes with it.
          </p>
          <div className="flex justify-end gap-2">
            <Button onClick={() => setConfirmDelete(false)}>Cancel</Button>
            <Button variant="danger" onClick={remove} disabled={deleting}>
              {deleting ? 'Deleting…' : 'Delete'}
            </Button>
          </div>
        </div>
      </Dialog>

      <Dialog open={moveOpen} onClose={() => setMoveOpen(false)} title={`Move "${node.title}"`}>
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-ink-muted mb-1.5">New parent</label>
            <Select
              value={moveTarget}
              onChange={(e) => setMoveTarget(e.target.value)}
              className="text-xs"
            >
              <option value="">(top level)</option>
              {moveCandidates.map((candidate) => (
                <option key={candidate.id} value={candidate.path}>
                  {' '.repeat(candidate.depth * 2)}{candidate.title} — {candidate.path}
                </option>
              ))}
            </Select>
          </div>
          <div className="flex justify-end gap-2">
            <Button onClick={() => setMoveOpen(false)}>Cancel</Button>
            <Button variant="primary" onClick={move} disabled={moving}>{moving ? 'Moving…' : 'Move'}</Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function Kb() {
  const params = useParams();
  const navigate = useNavigate();
  // The tree is owned by KbTreeProvider (mounted on the KB routes) and rendered by the pages panel
  // beside the document; this page reads `allNodes` for the move picker and calls `reload` so an
  // edit here settles the rows over there.
  const { projectId, projectSlug, allNodes, reload: reloadTree } = useKbTree();
  const basePath = `/p/${projectSlug}/kb`;

  // The node path is a splat param — it contains slashes, unlike every other route param here.
  const activePath = params['*'] ? decodeURIComponent(params['*']) : null;

  const [node, setNode] = useState<KbNodeDetail | null>(null);

  const loadNode = useCallback(() => {
    if (!projectId || !activePath) { setNode(null); return; }
    api.kb.node(projectId, activePath).then(setNode).catch(() => setNode(null));
  }, [projectId, activePath]);

  useEffect(() => { loadNode(); }, [loadNode]);

  const select = (path: string) => navigate(`${basePath}/${path}`);

  return (
    <div className="flex h-screen overflow-hidden bg-surface">

      {/* Two columns beside the app sidebar: this project's pages, then the document. The sidebar
          navigates between projects; the knowledge base is a view *inside* one, so its page tree
          appears only here — see KbTreePanel. */}
      <div className="pl-[var(--sidebar-w)] pr-[var(--rail-w)] flex h-full w-full overflow-hidden">
        <KbTreePanel activePath={activePath} onSelect={select} />

        {/* Document panel */}
        <div className="flex-1 h-full overflow-hidden bg-card min-w-0">
          {node && projectId ? (
            <NodeDetail
              node={node}
              projectId={projectId}
              projectSlug={projectSlug}
              allNodes={allNodes}
              onReload={() => { reloadTree(); loadNode(); }}
              onDeleted={() => { setNode(null); reloadTree(); navigate(basePath); }}
              onSelect={select}
            />
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center px-6">
              <div className="w-14 h-14 rounded-2xl bg-orange-50 dark:bg-orange-950/30 flex items-center justify-center mb-4">
                <Folder size={24} className="text-orange-400" />
              </div>
              <p className="text-sm font-medium text-ink-secondary mb-1">
                {activePath ? 'That node no longer exists' : 'No node selected'}
              </p>
              <p className="text-xs text-ink-faint max-w-sm">
                Pick a page from the list on the left. To add something new, use{' '}
                <span className="text-ink-secondary">Drop context</span> — the agent decides which
                project and which page it belongs on.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
